/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { createContext, useContext, useEffect } from 'react';
import { Platform } from 'react-native';
import { useLocalSearchParams, usePathname } from 'expo-router';
import type { HotelsNavigationContextType } from './types';

export const HotelsNavigationContext = createContext<
  HotelsNavigationContextType | undefined
>(undefined);

export const useHotelsNavigation = (): HotelsNavigationContextType => {
  const ctx = useContext(HotelsNavigationContext);
  if (!ctx) {
    throw new Error(
      'useHotelsNavigation must be used inside HotelsNavigationContext.Provider',
    );
  }
  return ctx;
};

/**
 * Non-throwing variant for shared hooks that may run outside the provider
 * (e.g. the home package). Returns undefined when no provider is mounted.
 */
export const useOptionalHotelsNavigation = ():
  | HotelsNavigationContextType
  | undefined => useContext(HotelsNavigationContext);

export const useHotelsSearchParams = (): Record<
  string,
  string | string[] | undefined
> => {
  const ctx = useContext(HotelsNavigationContext);
  const isTestEnv =
    typeof jest !== 'undefined' || process.env.NODE_ENV === 'test';

  if (!ctx) {
    if (isTestEnv) {
      return useLocalSearchParams();
    }
    if (Platform.OS === 'web') {
      if (typeof window !== 'undefined') {
        const searchParams = new URLSearchParams(window.location.search);
        return Object.fromEntries(searchParams.entries());
      }
      return {};
    }
    return useLocalSearchParams();
  }
  return ctx.useSearchParams();
};

export const useHotelsPathname = (): string => {
  const ctx = useContext(HotelsNavigationContext);
  const isTestEnv =
    typeof jest !== 'undefined' || process.env.NODE_ENV === 'test';

  if (!ctx) {
    if (isTestEnv) {
      return usePathname();
    }
    if (Platform.OS === 'web') {
      if (typeof window !== 'undefined') {
        return window.location.pathname;
      }
      return '/';
    }
    return usePathname();
  }
  return ctx.usePathname();
};

export const useHotelsFocusEffect = (
  effect: () => void | (() => void),
): void => {
  const ctx = useContext(HotelsNavigationContext);
  const isTestEnv =
    typeof jest !== 'undefined' || process.env.NODE_ENV === 'test';

  if (!ctx) {
    if (isTestEnv || Platform.OS === 'web') {
      // On Web or test env, use standard React useEffect
      useEffect(effect, []);
      return;
    }
    // On Native app, dynamically require expo-router
    const { useFocusEffect: nativeUseFocusEffect } = require('expo-router');
    nativeUseFocusEffect(effect);
    return;
  }
  ctx.useFocusEffect(effect);
};
